// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import xHeaderUpdate from "@werner/App/Handlers/XNG/Header";
import { CustomAppState } from "@werner/App/Store/Reducers";
import Close from "@werner/XDL/Icons/Close";
import { color, ellipsis, getSystemColor, layoutContainer, xScreen } from "@werner/XDL/Utilities";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    backgroundColor = "backgroundColor",
    message = "message",
    dismissible = "dismissible",
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.backgroundColor]: string;
        [fields.message]: string;
        [fields.dismissible]: boolean;
    };
}
type Props = OwnProps;
export interface GlobalWebsiteMessageStyles {
    container?: InjectableCss;
    wrapper?: InjectableCss;
    messageWrapper?: InjectableCss;
    closeClickable?: ClickablePresentationProps;
    closeIcon?: IconPresentationProps;
    message?: TypographyPresentationProps;
}
export const globalWebsiteMessageStyles: GlobalWebsiteMessageStyles = {
    wrapper: {
        css: css`
            ${({ backgroundColor }: any) =>
                backgroundColor &&
                css`
                    background: ${backgroundColor};
                `}
        `,
    },
    messageWrapper: {
        css: css`
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
            height: 50px;
            ${layoutContainer}
        `,
    },
    closeClickable: {
        css: css`
            &:focus {
                outline: none;
            }
            &:hover > ${IconWrapper} {
                color: ${color("text.main")};
            }
            ${xScreen("lte", "md")} {
                right: 24px;
            }
        `,
    },
    closeIcon: {
        color: "common.ui3",
        src: Close,
        size: 11,
    },
    message: {
        color: "common.ui1",
        variant: "p",
        size: 14,
        weight: 400,
        css: css`
            line-height: 16px;
            text-align: center;
            width: 100%;
            margin-bottom: 0;
            & > ${TypographyStyle} {
                font-size: 14px;
                color: ${color("text.brand2")};
                font-weight: 400;
                line-height: 16px;
                margin-bottom: 0;
                max-width: 90%;
                ${ellipsis(2)}
            }
            ${xScreen("lte", "xs")} {
                text-align: left;
            }
        `,
    },
};
const styles = globalWebsiteMessageStyles;
const GlobalWebsiteMessage: FC<Props> = ({ fields: { backgroundColor, message, dismissible } }) => {
    const dispatch = useAppDispatch();
    const { showGlobalMessage } = useAppSelector(state => ({
        showGlobalMessage: (state as CustomAppState).xHeader.showGlobalMessage,
    }));
    const onClose = () => {
        dispatch(xHeaderUpdate({ key: "showGlobalMessage", value: false }));
    };
    if (!showGlobalMessage) {
        return null;
    }
    return (
        <StyledWrapper {...styles.wrapper} {...{ backgroundColor }} data-test-selector="global-message-widget">
            <StyledWrapper {...styles.messageWrapper}>
                <Typography {...styles.message} data-test-selector="global_site_message">
                    {parse(message, parserOptions)}
                </Typography>
                {dismissible && (
                    <Clickable
                        {...styles.closeClickable}
                        onClick={onClose}
                        data-test-selector="global_site_message_close"
                        aria-label="Global Site Message Close Clickable"
                    >
                        <Icon {...styles.closeIcon} />
                    </Clickable>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};
const widgetModule: WidgetModule = {
    component: GlobalWebsiteMessage,
    definition: {
        group: "Xngage Custom" as any,
        displayName: "Global Website Message",
        allowedContexts: ["Header"],
        fieldDefinitions: [
            {
                name: fields.backgroundColor,
                displayName: "Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: getSystemColor("common.ui2"),
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.message,
                displayName: "Global Message Content",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
                placeholder: "Add your message here",
                isRequired: true,
            },
            {
                name: fields.dismissible,
                displayName: "Dismissible?",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isRequired: false,
            },
        ],
    },
};
export default widgetModule;
